var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useState, useCallback } from 'react';
import { CatalogProcessStatus } from '../../models/catalogs/CatalogProcess';
import { getProductIngestStatus } from '../../services/api/product-ingest-status-service';
import { getPriceIngestStatus } from '../../services/api/price-ingest-status-service';
import { getDataLakeStatus } from '../../services/api/data-lake-status-service';
import { getConfiguredHierarchyStatus } from '../../services/api/configured-hierarchy-status';
import { buildPriceSyncStatus, buildProductSyncStatus, buildKnollSyncStatus, buildConfiguredHierarchyStatus, buildDataLakeProductStatus, buildDataLakePriceStatus, } from './catalogStatusHelpers';
/**
 * Custom hook to manage catalog status data loading and state
 */
export function useCatalogStatus() {
    var _this = this;
    var _a = useState(), catalogStatus = _a[0], setCatalogStatus = _a[1];
    var _b = useState(false), loading = _b[0], setLoading = _b[1];
    var _c = useState(null), error = _c[0], setError = _c[1];
    /**
     * Loads catalog status from all API endpoints
     */
    var loadCatalogStatus = useCallback(function () { return __awaiter(_this, void 0, Promise, function () {
        var _a, productStatusResponse, priceStatusResponse, configuredHierarchyResponse, dataLakeStatusResponse, productStatus, priceStatus, dataLakeStatus, hierarchyStatus, errorStatus;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, Promise.all([
                        getProductIngestStatus(),
                        getPriceIngestStatus(),
                        getConfiguredHierarchyStatus(),
                        getDataLakeStatus()
                    ])];
                case 1:
                    _a = _b.sent(), productStatusResponse = _a[0], priceStatusResponse = _a[1], configuredHierarchyResponse = _a[2], dataLakeStatusResponse = _a[3];
                    productStatus = productStatusResponse.status === 200 ? productStatusResponse.body : undefined;
                    priceStatus = priceStatusResponse.status === 200 ? priceStatusResponse.body : undefined;
                    dataLakeStatus = dataLakeStatusResponse.status === 200 ? dataLakeStatusResponse.body : undefined;
                    hierarchyStatus = configuredHierarchyResponse.status === 200 ? configuredHierarchyResponse.body : undefined;
                    errorStatus = {
                        status: CatalogProcessStatus.UNKNOWN,
                        executionInProgress: false,
                        fullPayload: {},
                    };
                    return [2 /*return*/, {
                            cpqSyncStatus: priceStatus
                                ? buildPriceSyncStatus(priceStatus.cpqSync, priceStatus.priceIngest)
                                : errorStatus,
                            productSyncStatus: productStatus
                                ? buildProductSyncStatus(productStatus.productIngest)
                                : errorStatus,
                            knollSyncStatus: productStatus
                                ? buildKnollSyncStatus(productStatus.knollLoad)
                                : errorStatus,
                            configuredHierarchyStatus: hierarchyStatus
                                ? buildConfiguredHierarchyStatus(hierarchyStatus.configuredHierarchy)
                                : errorStatus,
                            dataLakeProductStatus: dataLakeStatus
                                ? buildDataLakeProductStatus(dataLakeStatus.product)
                                : errorStatus,
                            dataLakePriceStatus: dataLakeStatus
                                ? buildDataLakePriceStatus(dataLakeStatus.priceMart)
                                : errorStatus,
                        }];
            }
        });
    }); }, []);
    /**
     * Reloads catalog status data
     */
    var reload = useCallback(function () {
        setLoading(true);
        setError(null);
        setCatalogStatus(undefined);
        loadCatalogStatus()
            .then(function (result) {
            setCatalogStatus(result);
            setError(null);
        })
            .catch(function (err) {
            console.error('Failed to load catalog status:', err);
            setError(err instanceof Error ? err : new Error('Failed to load catalog status'));
        })
            .finally(function () {
            setLoading(false);
        });
    }, [loadCatalogStatus]);
    return {
        catalogStatus: catalogStatus,
        loading: loading,
        error: error,
        reload: reload,
    };
}
